'use client'

import { type KeyboardEvent } from 'react'

import { useEnhancedFormContext } from '@/shared/lib'
import { Button, Form } from '@/shared/ui'

import { BankSelector } from './bank-selector'
import { ErrorMessage } from './error-message'
import { useBankCodes } from '../api/get-bank-codes'
import { useAccountVerification } from '../lib/account-verification'

type Props = {
  onEditButtonClick: () => void
}

export const AccountForm = ({ onEditButtonClick }: Props) => {
  const {
    clearErrors,
    formState: { errors },
    setValue,
  } = useEnhancedFormContext()

  const { banks } = useBankCodes()

  const {
    verifyAccount,
    isAccountVerified,
    isVerificationFailed,
    markAsUnverified,
  } = useAccountVerification()

  const handleAccountInputChange = () => {
    if (errors?.bankAccount) {
      clearErrors('bankAccount')
    }

    if (errors?.accountHolder) {
      setValue('accountHolder', '')
      clearErrors(['root.accountServerError', 'accountHolder'])
      markAsUnverified()
    }
  }

  const handleAccountInputKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter') {
      void verifyAccount()
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <BankSelector banks={banks} readOnly={isAccountVerified} />
        <Form.Field name="bankAccount" className="flex-grow">
          <Form.TextField
            type="number"
            placeholder="계좌번호는 숫자만 입력해 주세요"
            fullWidth
            readOnly={isAccountVerified}
            onChange={handleAccountInputChange}
            onKeyDown={handleAccountInputKeyDown}
          />
        </Form.Field>
      </div>

      <Form.Field name="accountHolder" className="flex-grow">
        <Form.TextField
          placeholder="예금주명"
          fullWidth
          readOnly={!isVerificationFailed}
          resetErrorOnBlur={false}
        />
      </Form.Field>

      {isAccountVerified ? (
        <Button
          type="button"
          variant="outline"
          shape="pill"
          size="medium"
          fullWidth
          onClick={onEditButtonClick}
        >
          수정
        </Button>
      ) : (
        <Button
          type="button"
          variant="solid"
          shape="pill"
          size="medium"
          fullWidth
          onClick={verifyAccount}
          disabled={isVerificationFailed}
        >
          예금주 확인
        </Button>
      )}

      <ErrorMessage />
    </div>
  )
}
